import React, { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import DescriptionIcon from '@material-ui/icons/Description'
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'

import { LoriIcon } from '../../../navigation/layouts/Lori/Icons/LoriIcon'

import { Container } from '../../../components'
import {
    deleteAssetProductDocument,
    dismissProductSuggestion,
    formatDocumentTypeLabel,
    InventoryAsset,
    ProductDocument,
    ProductDocumentType,
    SuggestedDocument,
    suggestProductDocuments,
    uploadAssetProductDocument,
} from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    asset: InventoryAsset
    brand: string
    modelNumber: string
    onAssetUpdated: (asset: InventoryAsset) => void
}

const DOCUMENT_TYPE_OPTIONS: { value: ProductDocumentType; label: string }[] = [
    { value: 'manual', label: 'Manual' },
    { value: 'warranty', label: 'Warranty' },
    { value: 'spec_sheet', label: 'Spec sheet' },
    { value: 'other', label: 'Other' },
]

export const AssetProductDocuments: React.FC<Props> = ({
    asset,
    brand,
    modelNumber,
    onAssetUpdated,
}) => {
    const theme = useTheme()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [uploading, setUploading] = useState(false)
    const [deletingId, setDeletingId] = useState<number | null>(null)
    const [docName, setDocName] = useState('')
    const [docType, setDocType] = useState<ProductDocumentType>('manual')
    const [refreshingSuggestions, setRefreshingSuggestions] = useState(false)
    const [dismissingId, setDismissingId] = useState<number | null>(null)

    const documents = asset.product_profile?.documents ?? []
    const suggestedDocuments = asset.product_profile?.suggested_documents ?? []
    const hasBrandModel = Boolean(brand.trim() && modelNumber.trim())
    const showSuggestions = suggestedDocuments.length > 0

    const handleRefreshSuggestions = useCallback(async () => {
        setRefreshingSuggestions(true)
        try {
            const response = await suggestProductDocuments(asset.id, {
                async: false,
                force: false,
            })
            onAssetUpdated(response.data)
        } catch (err) {
            console.error(err)
            toast.error('Failed to refresh suggestions')
        } finally {
            setRefreshingSuggestions(false)
        }
    }, [asset.id, onAssetUpdated])

    const handleDismissSuggestion = useCallback(
        async (suggestionId: number) => {
            const profileId = asset.product_profile?.id
            if (!profileId) return

            setDismissingId(suggestionId)
            try {
                await dismissProductSuggestion(profileId, suggestionId)
                onAssetUpdated({
                    ...asset,
                    product_profile: asset.product_profile
                        ? {
                              ...asset.product_profile,
                              suggested_documents: asset.product_profile.suggested_documents.filter(
                                  (d) => d.id !== suggestionId,
                              ),
                          }
                        : null,
                })
            } catch (err) {
                console.error(err)
                toast.error('Failed to dismiss suggestion')
            } finally {
                setDismissingId(null)
            }
        },
        [asset, onAssetUpdated],
    )

    const handleUpload = async (file: File) => {
        if (!hasBrandModel) {
            toast.error(
                'Brand and model number are required to upload documents',
            )
            return
        }

        setUploading(true)
        try {
            const response = await uploadAssetProductDocument(asset.id, file, {
                name: docName.trim() || undefined,
                document_type: docType,
                brand: asset.brand ? undefined : brand.trim(),
                model_number: asset.model_number
                    ? undefined
                    : modelNumber.trim(),
            })
            onAssetUpdated(response.data.asset)
            setDocName('')
            toast.success('Document uploaded')
        } catch (err) {
            console.error(err)
            toast.error('Failed to upload document')
        } finally {
            setUploading(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            handleUpload(file)
        }
    }

    const handleDelete = async (documentId: number) => {
        setDeletingId(documentId)
        try {
            const response = await deleteAssetProductDocument(
                asset.id,
                documentId,
            )
            onAssetUpdated(response.data.asset)
            toast.success('Document removed')
        } catch (err) {
            console.error(err)
            toast.error('Failed to delete document')
        } finally {
            setDeletingId(null)
        }
    }

    const sectionTitleStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle2),
        fontWeight: 700,
        color: theme.palette.text.primary,
        letterSpacing: '0.02em',
    }

    return (
        <>
            <Divider />
            <Container
                style={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: theme.spacing(1.5),
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <LibraryBooksIcon
                        style={{
                            fontSize: 18,
                            color: theme.palette.info.main,
                        }}
                    />
                    <span style={sectionTitleStyle}>Product documents</span>
                    {documents.length > 0 && (
                        <Chip
                            size="small"
                            label={`${documents.length}`}
                            style={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                backgroundColor: `${theme.palette.info.main}15`,
                                color: theme.palette.info.main,
                            }}
                        />
                    )}
                </Container>

                {documents.length > 0 && (
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: 0,
                            borderRadius: 10,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            overflow: 'hidden',
                        }}
                    >
                        {asset.product_profile && (
                            <Container
                                style={{
                                    padding: theme.spacing(1, 1.5),
                                    backgroundColor: theme.palette.grey[50],
                                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                    alignItems: 'center',
                                    gap: theme.spacing(0.75),
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 12,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Linked to
                                </span>
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {asset.product_profile.display_name}
                                </span>
                            </Container>
                        )}
                        {documents.map(
                            (document: ProductDocument, index: number) => (
                                <Container
                                    key={document.id}
                                    style={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: theme.spacing(1.5),
                                        padding: theme.spacing(1.25, 1.5),
                                        borderBottom:
                                            index < documents.length - 1
                                                ? `1px solid ${theme.palette.grey[100]}`
                                                : 'none',
                                        backgroundColor:
                                            theme.palette.background.paper,
                                    }}
                                >
                                    <Container
                                        style={{
                                            alignItems: 'center',
                                            gap: theme.spacing(1),
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <DescriptionIcon
                                            style={{
                                                fontSize: 18,
                                                color: theme.palette.grey[500],
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {document.name}
                                        </span>
                                        <Chip
                                            size="small"
                                            label={formatDocumentTypeLabel(
                                                document.document_type,
                                            )}
                                            style={{
                                                height: 20,
                                                fontSize: 10,
                                                fontWeight: 500,
                                                backgroundColor:
                                                    theme.palette.grey[100],
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        />
                                    </Container>
                                    <Container
                                        style={{
                                            gap: 4,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            startIcon={
                                                <OpenInNewIcon
                                                    style={{ fontSize: 14 }}
                                                />
                                            }
                                            onClick={() =>
                                                window.open(
                                                    document.file,
                                                    '_blank',
                                                )
                                            }
                                            style={{
                                                textTransform: 'none',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                minWidth: 'unset',
                                                padding: theme.spacing(0.25, 1),
                                            }}
                                        >
                                            Open
                                        </Button>
                                        <Tooltip title="Delete document">
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    deletingId === document.id
                                                }
                                                onClick={() =>
                                                    handleDelete(document.id)
                                                }
                                            >
                                                {deletingId === document.id ? (
                                                    <CircularProgress
                                                        size={14}
                                                    />
                                                ) : (
                                                    <DeleteOutlineIcon
                                                        style={{
                                                            fontSize: 16,
                                                            color:
                                                                theme.palette
                                                                    .error
                                                                    .light,
                                                        }}
                                                    />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                </Container>
                            ),
                        )}
                    </Container>
                )}

                {/* AI suggested documents */}
                {showSuggestions && (
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Container
                            style={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Container
                                style={{
                                    alignItems: 'center',
                                    gap: theme.spacing(0.75),
                                }}
                            >
                                <LoriIcon width={16} height={16} />
                                <span
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Suggested manuals &amp; warranty (Lori)
                                </span>
                            </Container>
                            <Tooltip title="Refresh suggestions">
                                <IconButton
                                    size="small"
                                    disabled={refreshingSuggestions}
                                    onClick={handleRefreshSuggestions}
                                    style={{ padding: 4 }}
                                >
                                    {refreshingSuggestions ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <AutorenewIcon
                                            style={{ fontSize: 16 }}
                                        />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Container>
                        <Container
                            style={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 0,
                                borderRadius: 10,
                                border: `1px solid ${theme.palette.warning.light}40`,
                                backgroundColor: `${theme.palette.warning.light}08`,
                                overflow: 'hidden',
                            }}
                        >
                            {suggestedDocuments.map(
                                (doc: SuggestedDocument, index: number) => (
                                    <Container
                                        key={doc.id}
                                        style={{
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: theme.spacing(1),
                                            padding: theme.spacing(1, 1.5),
                                            borderBottom:
                                                index <
                                                suggestedDocuments.length - 1
                                                    ? `1px solid ${theme.palette.grey[100]}`
                                                    : 'none',
                                        }}
                                    >
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                gap: theme.spacing(1),
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <DescriptionIcon
                                                style={{
                                                    fontSize: 18,
                                                    color:
                                                        theme.palette.warning
                                                            .main,
                                                }}
                                            />
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {doc.title}
                                            </span>
                                            <Chip
                                                size="small"
                                                label={formatDocumentTypeLabel(
                                                    doc.document_type,
                                                )}
                                                style={{
                                                    height: 20,
                                                    fontSize: 10,
                                                    fontWeight: 500,
                                                    backgroundColor: `${theme.palette.warning.main}12`,
                                                    color:
                                                        theme.palette.warning
                                                            .dark,
                                                }}
                                            />
                                            <Chip
                                                size="small"
                                                label={`${Math.round(
                                                    doc.confidence * 100,
                                                )}%`}
                                                style={{
                                                    height: 18,
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    backgroundColor:
                                                        theme.palette.grey[100],
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            />
                                        </Container>
                                        <Container
                                            style={{
                                                gap: 4,
                                                alignItems: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                startIcon={
                                                    <OpenInNewIcon
                                                        style={{ fontSize: 14 }}
                                                    />
                                                }
                                                onClick={() =>
                                                    window.open(
                                                        doc.source_url,
                                                        '_blank',
                                                    )
                                                }
                                                style={{
                                                    textTransform: 'none',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    minWidth: 'unset',
                                                    padding: theme.spacing(
                                                        0.25,
                                                        1,
                                                    ),
                                                }}
                                            >
                                                Open
                                            </Button>
                                            <Tooltip title="Dismiss suggestion">
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        dismissingId === doc.id
                                                    }
                                                    onClick={() =>
                                                        handleDismissSuggestion(
                                                            doc.id,
                                                        )
                                                    }
                                                    style={{ padding: 4 }}
                                                >
                                                    {dismissingId === doc.id ? (
                                                        <CircularProgress
                                                            size={14}
                                                        />
                                                    ) : (
                                                        <CloseIcon
                                                            style={{
                                                                fontSize: 16,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .disabled,
                                                            }}
                                                        />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Container>
                                    </Container>
                                ),
                            )}
                        </Container>
                        <span
                            style={{
                                fontSize: 11,
                                color: theme.palette.text.disabled,
                                fontStyle: 'italic',
                            }}
                        >
                            Links are suggested by Lori; verify before relying
                            on them.
                        </span>
                    </Container>
                )}

                {/* Upload section */}
                <Container
                    style={{
                        padding: theme.spacing(1.5),
                        borderRadius: 10,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        backgroundColor: theme.palette.grey[50],
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: theme.spacing(1.5),
                    }}
                >
                    {!hasBrandModel ? (
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Fill in Brand and Model number above to upload
                            product documents.
                        </span>
                    ) : (
                        <>
                            <Container
                                style={{
                                    gap: theme.spacing(1),
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    label="Document name"
                                    placeholder="Optional"
                                    value={docName}
                                    onChange={(e) => setDocName(e.target.value)}
                                    style={{ flex: 1, minWidth: 140 }}
                                />
                                <TextField
                                    select
                                    size="small"
                                    variant="outlined"
                                    label="Type"
                                    value={docType}
                                    onChange={(e) =>
                                        setDocType(
                                            e.target
                                                .value as ProductDocumentType,
                                        )
                                    }
                                    style={{ minWidth: 120 }}
                                >
                                    {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                        uploading ? (
                                            <CircularProgress size={14} />
                                        ) : (
                                            <CloudUploadIcon />
                                        )
                                    }
                                    disabled={uploading}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    style={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        borderRadius: 8,
                                    }}
                                >
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </Button>
                            </Container>
                            <span
                                style={{
                                    fontSize: 11,
                                    color: theme.palette.text.disabled,
                                }}
                            >
                                Documents are shared across all units with
                                matching brand &amp; model.
                            </span>
                        </>
                    )}
                </Container>

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={handleFileSelect}
                />
            </Container>
        </>
    )
}
